import "../../../shared/styles/list.css";

import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Space,
  Switch,
  Table,
  TablePaginationConfig,
  Typography,
  Upload,
  UploadProps,
  Image,
} from "antd";
import {
  ColumnsType,
  FilterValue,
  SorterResult,
} from "antd/lib/table/interface";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { PlusCircleTwoTone, UploadOutlined } from "@ant-design/icons";

import { getPaginatedBanners, updateBanner } from "../../../services/bannerApi";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import ContainerLayout from "../../common/containerLayout";
import { selectCurrentAdminToken } from "../../../utils/helpers/selectorHelper";
import { RcFile } from "antd/es/upload";

const { Text } = Typography;

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters: Record<string, FilterValue | null>;
}

export interface BannerDTO {
  id?: number;
  name?: string;
  url?: string;
  fileKey?: string;
  forManager?: boolean;
  forApp?: boolean;
  active?: boolean;
  createdAt?: string;
  link?: string;
}

const validateFilters = (
  filters: Record<string, FilterValue | null>,
): boolean | undefined => {
  if (!filters.active) {
    return undefined;
  }

  if (filters.active.length === 1) {
    return Boolean(filters.active[0]);
  }

  return undefined;
};

const apiUrl = process.env.REACT_APP_API_URL;

const isJpgOrPng = (file: File) =>
  file.type === "image/jpeg" || file.type === "image/png";

const wrongFileNotification = () =>
  notification.error({
    message: "Solo se aceptan archivos con formato JPG y PNG",
    placement: "bottom",
    key: "global",
  });

const BannerList = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm<BannerDTO>();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();
  const [openModal, setOpenModal] = useState(false);

  const columns: ColumnsType<BannerDTO> = [
    {
      title: "Nombre",
      dataIndex: "name",
      sorter: false,
      width: "20%",
    },
    {
      title: "Imagen",
      dataIndex: "url",
      width: "30%",
      render: (url) => (
        <a href={url} target="_blank" rel="noreferrer">
          {url}
        </a>
      ),
    },
    {
      title: "Ver en manager",
      dataIndex: "forManager",
      render: (forManager) => (forManager ? "Si" : "No"),
      width: "10%",
    },
    {
      title: "Ver en app",
      dataIndex: "forApp",
      render: (forApp) => (forApp ? "Si" : "No"),
      width: "10%",
    },
    {
      title: "Link",
      dataIndex: "link",
      width: "10%",
      render: (url) => (
        <a href={url} target="_blank" rel="noreferrer">
          {url}
        </a>
      ),
    },
    {
      title: "Estado",
      dataIndex: "active",
      render: (active) =>
        active ? (
          <Text type="success">Activo</Text>
        ) : (
          <Text type="danger">Inactivo</Text>
        ),
      filters: [
        { text: "Activo", value: true },
        { text: "Inactivo", value: false },
      ],
    },
    {
      title: "Acciones",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            variant="solid"
            type="primary"
            onClick={() => handleOpenModal(record)}
          >
            Editar
          </Button>
        </Space>
      ),
    },
  ];

  const uploadProps: UploadProps = {
    listType: "picture",
    maxCount: 1,
    headers: {
      auth: selectCurrentAdminToken(),
    },
    beforeUpload: (file: RcFile) => beforeUpload(file),
    accept: ".jpeg,.png",
    action: `${apiUrl}files/upload/admin`,
  };

  const beforeUpload = async (file: RcFile) => {
    if (!isJpgOrPng(file)) {
      wrongFileNotification();
      return false;
    }
    return true;
  };

  const handleOpenModal = (record: BannerDTO) => {
    form.setFieldsValue({
      id: record.id,
      name: record.name,
      url: record.url,
      forManager: record.forManager,
      forApp: record.forApp,
      active: record.active,
      fileKey: record.fileKey,
      link: record.link,
    });
    setOpenModal(true);
  };

  const [tableParams, setTableParams] = useState<TableParams>({
    filters: {
      active: [],
    },
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  const fetchData = async () => {
    setLoading(true);

    const { data, total } = await getPaginatedBanners({
      page: tableParams.pagination?.current || 1,
      take: tableParams.pagination?.pageSize || 10,
      status: validateFilters(tableParams.filters),
    });

    setData(data);
    setLoading(false);
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        total,
      },
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tableParams)]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<BannerDTO> | SorterResult<BannerDTO>[],
  ) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
  };

  const handleCancel = () => {
    form.resetFields();
    setOpenModal(false);
  };

  const handleOk = async (values: any) => {
    const { url } = values;
    const urlFile = url?.file;
    let body = values;
    let fileUrl = "";

    if (urlFile) {
      fileUrl = urlFile.response.location;
      body = {
        ...body,
        url: fileUrl,
        fileKey: "doc-admin-" + Date.now() + "-" + urlFile.name,
      };
    }

    try {
      await updateBanner(form.getFieldValue("id"), body);
    } catch {
      notification.error({
        message: "Ha ocurrido un error al actualizar el banner.",
        key: "global",
      });
    } finally {
      form.resetFields();
      setOpenModal(false);
      fetchData();
    }
  };

  return (
    <ContainerLayout title="Banners" showNotification={false}>
      <Col span={24}>
        <Button
          onClick={() =>
            navigate(WebRoutes.CreateBannerRoute, {
              replace: true,
            })
          }
          icon={<PlusCircleTwoTone />}
          type="primary"
        >
          Banner
        </Button>
      </Col>
      <Col span={24} style={{ width: "100px", height: "83vh" }}>
        <Table
          columns={columns}
          locale={{ emptyText: "No hay datos" }}
          rowKey={(record) => record.id!}
          dataSource={data}
          pagination={tableParams.pagination}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ y: "calc(-250px + 99vh)" }}
        />
        <Modal
          title="Editar banner"
          open={openModal}
          onOk={form.submit}
          onCancel={handleCancel}
        >
          <Form
            form={form}
            onFinish={handleOk}
            layout="vertical"
            name="editBanner"
          >
            <Form.Item label="Nombre" name="name">
              <Input />
            </Form.Item>
            <Form.Item label="Enlace" name="link">
              <Input />
            </Form.Item>
            <Row justify="space-evenly">
              <Form.Item label="Visualizar en manager" name="forManager">
                <Switch />
              </Form.Item>
              <Form.Item label="Visualizar en cliente app" name="forApp">
                <Switch />
              </Form.Item>
              <Form.Item label="Activo" name="active">
                <Switch />
              </Form.Item>
            </Row>
            {form.getFieldValue("url") && (
              <Form.Item>
                <Row justify="center">
                  <Image
                    src={form.getFieldValue("url")}
                    style={{ maxWidth: "300px", maxHeight: "200px" }}
                    alt="avatar"
                    preview={false}
                  />
                </Row>
              </Form.Item>
            )}

            <Form.Item label="Subir imagen" valuePropName="url" name="url">
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>Seleccionar imagen</Button>
              </Upload>
            </Form.Item>
            <Alert message="Resolución recomendada: 740 x 200 px" type="info" />
          </Form>
        </Modal>
      </Col>
    </ContainerLayout>
  );
};

export default BannerList;
